/**
 * Determines if personalisation options are available based on steps, slots, and product availability.
 * @param {object} props - The parameters object.
 * @param {Map<string, any>} props.stepsDevModel - Map of personalisation steps.
 * @param {Map<string, any>} props.slotsDevModel - Map of personalisation slots.
 * @param {Map<string, any>} props.componentGroupsDevModel - Map of component groups.
 * @param {Map<string, any>} props.variantsDevModel - Map of variants.
 * @returns {boolean} - Whether personalisation options are available.
 */
/** @type {(props: { stepsDevModel: Map<string, any>, slotsDevModel: Map<string, any>, componentGroupsDevModel: Map<string, any>, variantsDevModel: Map<string, any> }) => boolean} */
export function hasPersonalisationOptions({
  stepsDevModel,
  slotsDevModel,
  componentGroupsDevModel,
  variantsDevModel
}) {
  // Get all slots from all steps.
  const allStepIds = Array.from(stepsDevModel.values())
    .map(step => step.id)
    .filter(Boolean);

  // Early return if no steps.
  if (allStepIds.length === 0) {
    return false;
  }

  // Get all slots from steps.
  const allSlots = Array.from(slotsDevModel.values())
    .filter(slot => slot && allStepIds.includes(slot.stepId));

  // Check for at least one slot in any step.
  const hasAnySlots = allSlots.length > 0;

  // Early return if no slots.
  if (!hasAnySlots) {
    return false;
  }

  // Check if there's a base product configuration step.
  const hasBaseProductConfiguration = allStepIds.includes('base-product-configuration');

  // If there's a base product configuration step, always show personalisation options
  // because variant selection is required.
  if (hasBaseProductConfiguration) {
    return true;
  }

  // Check if there are any required slots, but ignore if the slot's id is "base-product-configuration-slot"
  const hasRequiredSlots = allSlots.some(
    slot => slot.isRequired === true && slot.id !== "base-product-configuration-slot"
  );

  // For component slots, check if any variants are available for sale.
  const componentSlots = allSlots.filter(slot =>
    slot.slotType === 'COMPONENT' &&
    Array.isArray(slot.allowedComponentGroups) &&
    slot.allowedComponentGroups.length > 0
  );

  let hasActiveProduct = false;

  if (componentSlots.length > 0) {
    // Get all variant IDs from component groups.
    const allVariantIds = new Set();

    componentSlots.forEach(slot => {
      slot.allowedComponentGroups.forEach(groupId => {
        const group = componentGroupsDevModel.get(groupId);
        if (group && Array.isArray(group.productVariantIds)) {
          group.productVariantIds.forEach(id => allVariantIds.add(id));
        }
      });
    });

    // Check if at least one variant is available for sale.
    hasActiveProduct = Array.from(allVariantIds).some(variantId => {
      const variant = variantsDevModel.get(variantId);
      return variant && variant.availableForSale === true;
    });
  }

  // Check if there are any non-component slots (like engraving, text, etc.)
  const hasNonComponentSlots = allSlots.some(slot => slot.slotType !== 'COMPONENT');

  // Final logic:
  // - If there are no required slots AND there are no active products AND no non-component slots, then false
  // - Otherwise, true if there are steps and slots
  if (!hasRequiredSlots && !hasActiveProduct && !hasNonComponentSlots) {
    return false;
  }

  return true;
}
